import React, {
  ForwardRefRenderFunction,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useState,
} from "react";

import { Grid } from "@mui/material";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import { FiUpload } from "react-icons/fi";

import Button, { Color } from "../../../../../../../../components/Form/Button";
import { getBase64 } from "../../../../../../../../util/base64";
import useStyles, {
  CSVIcon,
  CloseIcon,
  Dropzone,
  DropzoneButton,
  ErrorLine,
  ErrorTitle,
  FileName,
  LoadingContainer,
  LoadingMainDiv,
  LoadingProgress,
  LoadingTitle,
  ShowFile,
  ShowFileLeft,
  StorageBoxImportModal,
  TextButton,
  TextButtonFormat,
  TextButtonInfo,
} from "./styles";

interface FileData {
  file: File;
  name: string;
}

export interface UploadCSVErrors {
  [x: string]: string[];
}

interface UploadCSVFileProps {
  lineErrors?: UploadCSVErrors;
  generalErrors?: UploadCSVErrors;
  onConfirmUpload?: (uri: string, fileUploaded?: File) => void;
  onCloseModal?: () => void;
  onTryAgain?: () => void;
  onDownloadTemplate?: () => void;
  loadingUpload?: boolean;
}

export interface UploadCSVFileHandles {
  open: () => void;
  close: () => void;
}

const StorageBoxesImportModal: ForwardRefRenderFunction<
  UploadCSVFileHandles,
  UploadCSVFileProps
> = (props, ref) => {
  const {
    onDownloadTemplate,
    onConfirmUpload,
    onCloseModal: onCloseModalExternal,
    onTryAgain,
    lineErrors = {},
    generalErrors = {},
    loadingUpload,
  } = props;

  const [modalOpened, setModalOpened] = useState<boolean>(false);
  const [fileData, setFileData] = useState<FileData[]>([]);
  const existFile = !!fileData?.length;

  const { t: translate } = useTranslation();

  const classes = useStyles();

  const existErrors = Object.keys({ ...generalErrors, ...lineErrors }).length > 0;

  const onOpenModal = useCallback(() => {
    setModalOpened(true);
  }, []);
  const onCloseModal = useCallback(() => {
    setModalOpened(false);
    setFileData([]);
    if (onCloseModalExternal) onCloseModalExternal();
  }, []);

  useImperativeHandle(ref, () => ({
    open: onOpenModal,
    close: onCloseModal,
  }));

  const onDropFile = (files: File[]) => {
    if (!files?.length) return;

    const file = files[0];

    setFileData([
      {
        file,
        name: file.name,
      },
    ]);
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDropAccepted: onDropFile,
    maxFiles: 1,
    accept: [".csv", "text/csv"],
  });

  const renderDropzone = () => {
    return (
      <Dropzone {...getRootProps()}>
        <input {...getInputProps()} />
        <DropzoneButton justifyContent={"center"} alignItems={"center"}>
          <TextButton>
            <FiUpload size={24} />
          </TextButton>
          <TextButton>{translate("dragOrClickToUploadYourFile")}</TextButton>
          <TextButtonInfo>{`${translate("maxSize")} ${translate("2Mb")}`}</TextButtonInfo>
          <TextButtonInfo>
            {translate("formats")}: <TextButtonFormat>{translate("csv")}</TextButtonFormat>
          </TextButtonInfo>
        </DropzoneButton>
      </Dropzone>
    );
  };

  const onRemoveFileFromData = (fileIndex: number) => {
    const data = [...fileData];

    data.splice(fileIndex, 1);
    setFileData(data);
  };

  const showLineErrors = () => {
    if (!existErrors) return null;

    return (
      <Grid container direction="column">
        <ErrorTitle item xs={12}>
          <span>{translate("onUploadedCsvWeHaveFoundSomeErrors")}.</span>
        </ErrorTitle>
        <ErrorTitle item xs={12}>
          <span>{translate("youShouldAdjustThoseLinesToproceed")}:</span>
        </ErrorTitle>
        {Object.values(generalErrors).flatMap((error, index) => {
          return (
            <ErrorLine item xs={12} key={index}>
              <span>{error}</span>
            </ErrorLine>
          );
        })}
        {Object.keys(lineErrors).map((lineWithError) => {
          const errors = lineErrors[lineWithError] || [];

          return errors.map((error, index) => (
            <ErrorLine item xs={12} key={index}>
              <span>{`Line ${lineWithError} - ${error}`}</span>
            </ErrorLine>
          ));
        })}
      </Grid>
    );
  };

  const showFile = () => {
    if (existErrors) {
      return showLineErrors();
    }

    return fileData.map((fileContent, index) => (
      <ShowFile key={index}>
        <ShowFileLeft>
          <CSVIcon size={16} />
          <FileName>{fileContent.name}</FileName>
        </ShowFileLeft>

        <Button
          ghost={true}
          startIcon={<CloseIcon size={20} />}
          onClick={() => onRemoveFileFromData(index)}
        ></Button>
      </ShowFile>
    ));
  };

  const downloadTemplateButtonProps = onDownloadTemplate && {
    textSave: translate("downloadTemplate"),
    onSave: () => onDownloadTemplate(),
    saveDisabled: loadingUpload,
    customColorSave: Color.DEFAULT,
    classesSave: classes.downloadButtonMargin0,
  };

  const renderLoading = () => {
    return (
      <LoadingMainDiv>
        <LoadingContainer>
          <LoadingTitle>{translate("weAreLoadingTheUploadedContentPleaseWait")}</LoadingTitle>

          <LoadingProgress />
          <div></div>
        </LoadingContainer>
      </LoadingMainDiv>
    );
  };

  const renderContent = () => {
    return (
      <Grid container direction="row" alignItems="flex-start" spacing={2}>
        <Grid item sm={12}>
          {!existFile ? renderDropzone() : showFile()}
        </Grid>
      </Grid>
    );
  };

  const onSubmitModal = async () => {
    if (!existFile) return;

    const uriCSV = await getBase64<string>(fileData[0].file, "text");

    if (onConfirmUpload) onConfirmUpload(uriCSV, fileData[0].file);
  };

  const tryAgain = () => {
    onTryAgain?.();
    setFileData([]);
  };

  return (
    <StorageBoxImportModal
      open={modalOpened}
      handleClose={!loadingUpload ? onCloseModal : undefined}
      title={!loadingUpload ? translate("importCsvFile") : ""}
      disableEnforceFocus
      data-cy="importStorageBox"
      dataCyTitle="importStorageBoxTitle"
      showActionButtons
      showLeftActionButtons
      actionLeftButtonProps={downloadTemplateButtonProps}
      actionButtonProps={{
        dataCyCancel: "cancelButton",
        onCancel: onCloseModal,
        customColorCancel: Color.TEXT_INFO,
        dataCySave: "submitChangesButton",
        textSave: existErrors ? translate("tryAgain2") : translate("import"),
        saveDisabled: !existFile || loadingUpload,
        cancelDisabled: loadingUpload,
        onSave: existErrors ? tryAgain : onSubmitModal,
      }}
    >
      {!loadingUpload ? renderContent() : renderLoading()}
    </StorageBoxImportModal>
  );
};

export default forwardRef(StorageBoxesImportModal);
